/**
 * branchTrigger.js — Pure builders: trigger descriptor → { assumption, modification, forkEvent }.
 *
 * `triggerToAssumption` is side-effect-free and fully unit-testable. App.jsx's three branch-creation
 * sites (createBranch + adapters) call this so their server-visible assumption strings are maintained
 * in one place.
 *
 * Supported trigger kinds:
 *   'event-mod'    — modify an existing event (probability / delete / date)
 *   'custom-event' — inject a brand-new user-described event
 *   'reforecast'   — pass-through (trigger already carries precomputed fields)
 */

import { fmtResolveDate } from "../formatDates.js";
import {
  branchDirContext,
  occurAssumption,
  notOccurAssumption,
  customEventAssumption,
} from "./branchAssumption.js";

/**
 * Midpoint (ms epoch) of a date-range event, or null when the event isn't a
 * range (no `date_end`, or `date_end === date`). Shared by the visual fork-date
 * computation here and the hidden-events screen-position computation in
 * App.jsx — both need the SAME range-midpoint math so a branch fork point and
 * the event's on-timeline position never diverge.
 * @param {{date: string, date_end?: string|null}} event
 * @returns {number|null}
 */
export function eventMidpointMs(event) {
  if (!event.date_end || event.date_end === event.date) return null;
  return (new Date(event.date).getTime() + new Date(event.date_end).getTime()) / 2;
}

/** Midday UTC, for a day the user NAMED rather than pointed at. Any hour would do; what
 *  matters is that it is not 00:00 — a midnight-anchored node lands at the very start of
 *  its day, which is how a trigger placed on the same day a forecast was generated ended
 *  up rendering to the LEFT of the GENERATED line. Midday is the honest centre of a day
 *  stated with no time in it. */
const NAMED_DAY_HOUR_MS = 12 * 3600 * 1000;

/**
 * The INSTANT a hand-placed trigger sits at, from the day the picker holds plus the
 * click that opened it.
 *
 * A custom trigger used to carry a day and nothing more, and a day becomes UTC midnight
 * the moment anything positions it (`new Date("2026-08-25")`). Two things broke on that:
 * the node rendered at the START of its day — up to 24h left of the GENERATED line for a
 * same-day placement — and its `isPast` flag was computed against that midnight rather
 * than against where the user actually clicked, which read `true` for the whole of the
 * day it was created on.
 *
 * The picker stays day-granular (it is an `<input type="date">`), so the time-of-day has
 * to come from somewhere else:
 *   • day unchanged from the click → the click's own instant, exactly where they pointed.
 *   • day re-picked → that day, keeping the click's time-of-day (a stated day carries no
 *     time, and reusing the one time-of-day in evidence beats inventing one).
 *   • no click instant at all (the chat `add_event` path names a day and never points at
 *     the timeline) → midday UTC.
 *
 * @param {object} args
 * @param {string|null} [args.day] Day the picker holds, `YYYY-MM-DD`.
 * @param {string|null} [args.clickIso] Full ISO instant of the double-click, if any.
 * @returns {string|null} Full ISO instant, or null when neither input is usable.
 */
export function resolveTriggerInstant({ day, clickIso } = {}) {
  const click = clickIso ? new Date(clickIso) : null;
  const clickOk = !!click && !isNaN(click.getTime());
  const dayStr = typeof day === "string" ? day.slice(0, 10) : null;
  if (!dayStr || !/^\d{4}-\d{2}-\d{2}$/.test(dayStr)) {
    return clickOk ? click.toISOString() : null;
  }
  const dayStartMs = new Date(`${dayStr}T00:00:00.000Z`).getTime();
  if (isNaN(dayStartMs)) return clickOk ? click.toISOString() : null;
  if (!clickOk) return new Date(dayStartMs + NAMED_DAY_HOUR_MS).toISOString();
  const clickIsoStr = click.toISOString();
  if (clickIsoStr.slice(0, 10) === dayStr) return clickIsoStr;
  // Re-picked day: carry the click's time-of-day onto it.
  const clickDayStartMs = new Date(`${clickIsoStr.slice(0, 10)}T00:00:00.000Z`).getTime();
  return new Date(dayStartMs + (click.getTime() - clickDayStartMs)).toISOString();
}

/**
 * Default id generator — mirrors App.jsx's uid() using crypto.randomUUID when available.
 * @returns {string}
 */
const defaultUid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

/**
 * Convert a branch trigger descriptor into the server-facing assumption string plus display fields.
 *
 * @param {object} trigger                  Branch trigger descriptor (see kind variants below)
 * @param {string} trigger.kind             'event-mod' | 'custom-event' | 'reforecast'
 *
 * event-mod fields:
 * @param {object} trigger.event            The event being modified (title, date, direction, …)
 * @param {string} trigger.modType          'probability' | 'delete' | 'date'
 * @param {number} [trigger.newValue]       New probability (0–1) or new date string for modType=date
 * @param {string} [trigger.forkDate]       Optional anchored fork-position override (date string).
 *                                          The adapter computes it via eventAnchorMs (it reads the
 *                                          slideCache ref, which a pure module can't); when present
 *                                          it wins over the range-midpoint-or-date `visualDate`.
 *
 * custom-event fields:
 * @param {string} trigger.description      User's event description
 * @param {string} trigger.date             Placement instant, full ISO (a bare YYYY-MM-DD
 *                                         still parses, but anchors the node at UTC midnight)
 *
 * reforecast fields (pass-through):
 * @param {string} trigger.assumption       Pre-computed assumption string
 * @param {string} trigger.modification     Pre-computed modification display string
 * @param {object|null} trigger.forkEvent   Pre-computed fork event (or null)
 * @param {string} [trigger.forkDate]       Pre-computed fork date
 *
 * @param {function} [uidFn]                Optional id generator (defaults to crypto.randomUUID-based uid)
 *
 * `forkDate` is the branch's visual fork position on the timeline. For event-mod it is
 * `trigger.forkDate` when the adapter supplies the anchored override (eventAnchorMs), else the
 * ORIGINAL event's visual position (midpoint of a date-range event, else its date) — NOT the
 * post-modification date, so a date-move branch still forks from where the event sat. For
 * custom-event it is the placement INSTANT (full ISO — see `resolveTriggerInstant`); for
 * reforecast it passes through `trigger.forkDate`.
 *
 * @returns {{ assumption: string, modification: string, forkEvent: object|null, forkDate: string|null }}
 */
export function triggerToAssumption(trigger, uidFn = defaultUid) {
  const { kind } = trigger;

  // ── event-mod ──────────────────────────────────────────────────────────────
  // Verbatim parity with genBranch() in App.jsx (lines 1575–1611).
  // The server forecasts on the `assumption` string — do not alter these.
  if (kind === "event-mod") {
    const { event: modEvent, modType, newValue } = trigger;

    // modification display string (desc in genBranch)
    const modification =
      modType === "delete"
        ? `Remove: "${modEvent.title}"`
        : modType === "date"
          ? `Move "${modEvent.title}" to ${newValue}`
          : newValue === 1
            ? `If "${modEvent.title}" happens`
            : newValue === 0
              ? `If "${modEvent.title}" doesn't happen`
              : `Change "${modEvent.title}" chance to ${Math.round(newValue * 100)}%`;

    // Direction clause reflects the SCENARIO's net effect, not just the trigger's
    // own direction: an occur branch keeps it; a does-not-occur / removed branch
    // inverts it (the event not happening pushes the forecast the opposite way).
    const occurCtx = branchDirContext(modEvent.direction);
    const absentCtx = branchDirContext(modEvent.direction, { absent: true });

    // What the premise is bounded BY: the END of a ranged trigger's window, not its start.
    // Formatted, because the server renders this date into the branch title verbatim.
    const byDate = fmtResolveDate(modEvent.date_end || modEvent.date);

    // Assumption string. The SENTENCE TEMPLATE is what must stay fixed — trigger_gen parses
    // it to rebuild the branch title and to sniff the branch sense — not the string byte for
    // byte: the date inside it is now written for a reader rather than left ISO.
    const assumption =
      modType === "delete"
        ? `Event "${modEvent.title}" has been removed from consideration.${absentCtx}`
        : modType === "probability"
          ? newValue >= 0.9
            ? occurAssumption({ title: modEvent.title, date: byDate, dirContext: occurCtx })
            : notOccurAssumption({
                title: modEvent.title,
                date: byDate,
                dirContext: absentCtx,
              })
          : `Event "${modEvent.title}" has been moved to ${fmtResolveDate(newValue)}.`;

    // Explicit branch sense (mirrors the assumption above) — the server anchors on
    // this instead of sniffing the assumption text, which misfires when an event
    // title embeds a sense phrase. delete → not_occurs; probability ≥0.9 → occurs,
    // else not_occurs; date → reschedule.
    const sense =
      modType === "delete"
        ? "not_occurs"
        : modType === "probability"
          ? newValue >= 0.9
            ? "occurs"
            : "not_occurs"
          : "reschedule";

    // The branch's visual fork POSITION — still the range midpoint, which is where the
    // timeline draws the event's circle, so the branch keeps descending from the node.
    // This is the fork's x-position only; it is no longer written onto the fork event.
    const _midpointMs = eventMidpointMs(modEvent);
    const visualDate =
      _midpointMs != null ? new Date(_midpointMs).toISOString().slice(0, 10) : modEvent.date;

    // forkEvent construction (verbatim from genBranch lines 1609–1611).
    // `_sourceEventId` records the CLICKED event's id so EventModal can suppress the
    // already-taken OCCURS / DOESN'T-OCCUR button for that event (App.jsx dedup at
    // ~:696). It is the original event's id (modEvent === trigger.event), NOT the
    // minted fork id — set on every modType variant.
    let forkEvent;
    if (modType === "delete") {
      forkEvent = {
        ...modEvent,
        id: "fk-" + uidFn(),
        _sourceEventId: modEvent.id,
        _deleted: true,
        // `date`/`date_end` are inherited from modEvent by the spread: a ranged trigger
        // KEEPS its window on the branch. It used to be collapsed to a midpoint point,
        // which threw away the one thing the node was communicating.
      };
    } else if (modType === "probability") {
      forkEvent = {
        ...modEvent,
        id: "fk-" + uidFn(),
        _sourceEventId: modEvent.id,
        probability: newValue,
        p_event: newValue,
        // Range inherited from modEvent by the spread — see the delete branch above.
        direction: newValue >= 0.5 ? "yes" : "no",
      };
    } else if (modType === "date") {
      forkEvent = {
        ...modEvent,
        id: "fk-" + uidFn(),
        _sourceEventId: modEvent.id,
        date: newValue,
        date_end: null,
      };
    }

    // forkDate is the branch's visual fork position on the timeline. The adapter
    // (handleBranch) can pass `trigger.forkDate` as an OVERRIDE — the anchored
    // position from eventAnchorMs (mean_date → slideCache → range-midpoint → date),
    // i.e. exactly where the timeline draws the event circle. A pure module can't
    // read the slideCache ref, so the override wins when present; otherwise we fall
    // back to visualDate (the range-midpoint-or-date computed above), which keeps
    // callers without an override unchanged.
    return { assumption, modification, forkEvent, forkDate: trigger.forkDate ?? visualDate, sense };
  }

  // ── custom-event ───────────────────────────────────────────────────────────
  // Verbatim parity with handleCustomEventBranch() in App.jsx (lines 4651–4693).
  if (kind === "custom-event") {
    const { description, date, contract, score } = trigger;
    const coherentScore = score?.status === "coherent" ? score : null;
    // TWO DIFFERENT DATES, and conflating them is a bug this code has already had:
    //
    //   clickedStr — WHERE THE USER CLICKED on the timeline. This is the node's position,
    //                and the only thing that may drive `forkDate` / `forkEvent.date`.
    //   closeStr   — when the trigger's own CONTRACT closes, from the preprocessor. This is
    //                the grading deadline; it drives `resolution_time` and the window's end.
    //
    // They are usually far apart: the preprocessor is told the trigger must resolve on or
    // before the PARENT's resolution date, so `closeStr` tends to land near the forecast
    // line. Using it for the node made every hand-placed branch snap to the right-hand end
    // of the timeline regardless of where the user clicked.
    // How the assertion READS. The user stated something, so state it back: the contract's
    // present-tense `assertion` when the server produced one, else the words they typed.
    //
    // The contract QUESTION is deliberately NOT in this chain, though it is more specific
    // than the typed fragment. This title is dropped into a clause slot — the server renders
    // "If <title>, will <parent question>?" — and an interrogative cannot sit there: it
    // produced 'If "Will X happen?" happens on …, will Y?', the nested-question shape this
    // work exists to remove. The user's own words are already declarative, so they degrade
    // gracefully where the question degrades into nonsense. The question keeps its real job
    // below, as the contract the resolver grades.
    const assertedTitle = contract?.assertion?.trim() || description.trim();
    // THREE forms of the placement, not two, and they are not interchangeable.
    // `date` now arrives as the full INSTANT the user placed the node at
    // (`resolveTriggerInstant`), which is what fixes a node that used to be pinned to
    // UTC midnight of its day — up to 24h LEFT of the GENERATED line for a trigger
    // placed on the same day the forecast was generated, and `isPast` true against a
    // clock hours past that midnight.
    //   clickedIso — the placement INSTANT. The ONLY thing that may drive `forkDate`,
    //                `forkEvent.date`, and `isPast`.
    //   clickedStr — its DAY. The only form the assumption SENTENCE may carry:
    //                trigger_gen.py's _BRANCH_CUSTOM_RE parses that sentence, and it
    //                is also what a reader wants ("…happens on 2026-08-25").
    //   closeStr   — the contract's grading deadline, day-granular by contract.
    const clickedIso = new Date(date).toISOString();
    const clickedStr = clickedIso.slice(0, 10);
    const closeStr = contract?.resolution_date
      ? String(contract.resolution_date).slice(0, 10)
      : clickedStr;

    // assumption string (verbatim from handleCustomEventBranch line 4693).
    // PHRASE SHAPE UNCHANGED — `customEventAssumption` is hand-synced with
    // trigger_gen.py's _BRANCH_CUSTOM_RE, so only the title inside it moves to the
    // contract's question. Rewording the sentence would break the server's
    // sense-sniff and the "If A, will B?" title rebuild.
    const assumption = customEventAssumption({
      // The date the user PLACED the event on — that is what they asserted, and what the
      // reader-facing "…happens on <date>" title should say.
      date: clickedStr,
      title: assertedTitle,
    });

    // modification display string (verbatim from handleCustomEventBranch line 4669)
    const modification = `If "${description.trim()}" happens`;

    // placeholder event (verbatim from handleCustomEventBranch line 4668)
    const forkEvent = {
      id: "ce-" + uidFn(),
      title: assertedTitle,
      description: "Custom user event",
      date: clickedIso,
      // The window the premise is graded over runs from where the user placed the event to
      // its contract's close — so a NO is not decisive until the deadline passes, matching
      // how a machine trigger's ranged window behaves. Only set when the close is genuinely
      // later; otherwise premiseWindow's end-of-clicked-day fallback is already right.
      ...(closeStr > clickedStr
        ? {
            date_end: closeStr,
            // The window is a GRADING DEADLINE, not a spread. A machine trigger's
            // date..date_end says "sometime in here", so the timeline centres its circle in
            // it and draws a distribution across it. This one says "I assert it happens on
            // `date`, and it is graded until `date_end`" — centring it walks the node off
            // toward the forecast line, and a band over it claims an uncertainty the user
            // did not express. Renderers key on this to anchor at `date` and skip the band;
            // premiseWindow still reads date_end, because grading genuinely runs to the close.
            _gradingWindow: true,
          }
        : {}),
      // A coherent score is copied verbatim. The checker owns acceptance; this projection
      // must not clamp, round, or reconcile any model-stated number. A failed/unscored
      // call keeps the historical placeholder so branch creation remains non-blocking.
      p_event: coherentScore ? coherentScore.w : 1.0,
      p_conditional: coherentScore ? coherentScore.p_conditional : 0.5,
      direction: coherentScore ? coherentScore.direction : "yes",
      ...(coherentScore
        ? {
            a: coherentScore.a,
            b: coherentScore.b,
            _scoreStatus: "coherent",
            _scoreResidual: coherentScore.residual,
          }
        : score
          ? {
              _scoreStatus: score.status,
              _scoreReason: score.reason,
              _scoreProblems: score.problems ?? [],
            }
          : {}),
      // Against the placement INSTANT, not its midnight. This used to read true for every
      // trigger placed on the CURRENT day (midnight had already gone by), and the mint gate
      // drops a past event — so a same-day custom trigger was never minted as a contract and
      // its branch's premise could never be graded at all.
      isPast: new Date(clickedIso) <= new Date(),
      source: "User",
      // The contract-grade trio, when the trigger has a contract. These are the
      // exact three fields the forecast server's trigger-contract gate reads: the
      // branch's run writes this typed premise's contract from them (chatboard's
      // server sends them as the branch's `premise`).
      ...(contract
        ? {
            question: contract.question,
            resolution_criteria: contract.resolution_criteria,
            resolution_time: closeStr,
          }
        : {}),
    };

    // forkDate is where the user CLICKED — never the contract's close, and now the
    // instant rather than the day, so the fork connector lands on the node's own circle
    // (both are positioned from this one value). Custom-event branches have no verb-form
    // phrase → sense "custom" (treat-as-certain).
    return { assumption, modification, forkEvent, forkDate: clickedIso, sense: "custom" };
  }

  // ── reforecast ─────────────────────────────────────────────────────────────
  // Pass-through: the trigger already carries precomputed fields.
  if (kind === "reforecast") {
    const { assumption, modification, forkEvent, forkDate } = trigger;
    // forkDate + sense pass through (the reforecast trigger carries them, or null);
    // a null sense leaves the server to sniff the carried assumption text.
    return {
      assumption,
      modification,
      forkEvent: forkEvent ?? null,
      forkDate: forkDate ?? null,
      sense: trigger.sense ?? null,
    };
  }

  throw new Error(`triggerToAssumption: unknown kind "${kind}"`);
}

/**
 * Branch soft-context: the parent's PRIOR that the server hands the
 * branch re-forecast to revise (instead of running cold) — the parent headline
 * p0, the report's reasons/aggregation, and the clicked trigger's a/b. Direction
 * is DERIVED server-side (sign(a−p0)), never sent. Trigger conditionals exist
 * only for an `event-mod` branch off a (w,a,b)-server trigger; parent_probability
 * and the reasons apply to any branch kind. Every field is optional server-side
 * (an all-empty block is a no-op there), so this always returns a plain object a
 * branch-creation path can pass straight through. Currently wired only at the
 * createBranch path (App.jsx → streamBranch); the reforecast RERUN path does not
 * yet send it, so a re-run branch is re-forecast cold (tracked as a follow-up).
 * @param {object|null} parent The parent RUN (reads .contract.probability, .report.reasons_*).
 * @param {object} trigger The branch descriptor ({ kind, event }).
 * @returns {object} The branch_context request payload.
 */
export function buildBranchContext(parent, trigger) {
  const modEvent = trigger?.kind === "event-mod" ? trigger.event : null;
  return {
    parent_probability: parent?.contract?.probability ?? null,
    reasons_for: parent?.report?.reasons_for ?? [],
    reasons_against: parent?.report?.reasons_against ?? [],
    aggregation: parent?.report?.aggregation ?? [],
    trigger_a: modEvent?.a ?? null,
    trigger_b: modEvent?.b ?? null,
  };
}
